import { createRef, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  pageLoadSuccess,
  setCoverPageLength,
  setCurrentPage,
  setPdfFileName,
  setPdfPages,
  setPreviousReportPdfPages,
  setPreviousReportPdfUrl,
  setTransmissionReportFileEnableCache,
  setTransmissionReportPdf,
  setTransmissionReportPdfUrl,
} from "app/store/transmission/slice/transmissionSlice";

import { pdfReport } from "app/services/transmission/report";
import { getFileName } from "app/utils/file";
import {
  USER_ROLES,
  HF_BILLING_CODES,
  MANUFACTURER_MAPPER,
  REPORT_STATES_MAPPER,
} from "static/constants";
import { showErrorToast } from "utils/toast";

const pdfPageRefs = createRef();
const previousPdfPageRefs = createRef();

pdfPageRefs.current = [];
previousPdfPageRefs.current = [];

const usePdfViewer = ({ reportId, isPreviousReport }) => {
  const {
    totalPdfPages,
    totalPreviousReportPdfPages,
    showNotes,
    openDrawer,
    pdfId,
    selectedAlert,
    isPreviousReportLegacy,
    currentPageNumber,
    currentPageNumberForPreviousReport,
    reportDetail,
    previousReportBillingCode,
    transmissionReportFileEnableCache,
    isPreviousReportModal,
    isPreviousAlertModal,
    autoScrollPageNumber,
    reportCurrentTab,
    reportAccountDetail,
    coverPageLength,
  } = useSelector((state) => state.transmission);
  const { currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const containerRef = useRef(null);
  const currentRef = isPreviousReport ? previousPdfPageRefs : pdfPageRefs;
  const [pdfWidth, setPdfWidth] = useState(0);
  const [pdfFile, setPdfFile] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isRegularPdfLoading, setIsRegularPdfLoading] = useState(false);

  const checkIsFetchingCoverPage = useCallback(() => {
    const isHFReport = HF_BILLING_CODES.includes(
      previousReportBillingCode || reportDetail?.billing_code
    );

    const settingToShowCoverPage =
      reportAccountDetail?.settings?.manufacturer?.[
        MANUFACTURER_MAPPER[reportDetail?.manufacturer]
      ]?.show_cover_page_on_tabs;

    const tabsToShowCoverPage =
      (isHFReport
        ? settingToShowCoverPage?.hf
        : settingToShowCoverPage?.regular) || [];

    const isFetchingCoverPage =
      isPreviousReportLegacy === false ||
      (reportDetail?.legacy === false &&
        [USER_ROLES.PHYSICIAN].includes(currentUser?.role) &&
        tabsToShowCoverPage?.includes(REPORT_STATES_MAPPER[reportCurrentTab]));

    return isFetchingCoverPage;
  }, [
    currentUser?.role,
    isPreviousReportLegacy,
    previousReportBillingCode,
    reportAccountDetail?.settings?.manufacturer,
    reportDetail?.billing_code,
    reportDetail?.legacy,
    reportDetail?.manufacturer,
    reportCurrentTab,
  ]);

  const fetchReportPdf = async (fetchCoverPage) => {
    try {
      setIsPdfLoading(true);

      const isHFReport = HF_BILLING_CODES.includes(
        previousReportBillingCode || reportDetail?.billing_code
      );

      const response = await pdfReport({
        reportId,
        isFetchingCoverPage: fetchCoverPage ?? false,
        isHFReport,
        enableCache: transmissionReportFileEnableCache,
      });

      if (response) {
        const file = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        const fileName = getFileName(response);
        const transmissionReportFile = new File([file], fileName, {
          type: file.type,
        });

        const fileHeader = response?.headers?.get("Content-Disposition");
        const coverPageLength = fileHeader?.split("coverPageLength=")?.[1] || 0;
        dispatch(setCoverPageLength(Number(coverPageLength)));

        let container = document.querySelector("#pdf-" + pdfId);
        if (!container) return false;

        const pdfContainer = container.querySelector(".view-pdf__document");
        const width = pdfContainer.offsetWidth;
        setPdfWidth(width);
        setPdfFile(fileURL);

        dispatch(setPdfFileName(fileName));
        dispatch(setTransmissionReportFileEnableCache(true));

        if (isPreviousReport) {
          dispatch(setPreviousReportPdfUrl(fileURL));
        } else {
          dispatch(setTransmissionReportPdfUrl(fileURL));
          dispatch(setTransmissionReportPdf(transmissionReportFile));
        }
      }
    } catch (err) {
      showErrorToast("Report doesn't exist in the system");

      setPdfFile(null);
      dispatch(setPdfFileName(null));
      if (!isPreviousAlertModal && !isPreviousReportModal) {
        dispatch(setTransmissionReportPdfUrl(null));
        dispatch(setTransmissionReportPdf(null));
      }
    } finally {
      setIsPdfLoading(false);
    }
  };

  const openRegularReportOnNewTab = async () => {
    try {
      setIsRegularPdfLoading(true);
      const response = await pdfReport({ reportId, view: "regular" });

      if (response) {
        const fileBlob = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(fileBlob);

        // Open the URL in a new tab
        window.open(fileURL, "_blank", "popup");
      }
    } catch (err) {
      showErrorToast(err?.message || "Report doesn't exist in the system.");
    } finally {
      setIsRegularPdfLoading(false);
    }
  };

  const onPDFDocumentLoadSuccess = (pdf) => {
    if (pdf) {
      const totalPdfPages = pdf?.numPages;
      !isPreviousReport && dispatch(setCurrentPage(1));
      isPreviousReport
        ? dispatch(setPreviousReportPdfPages(totalPdfPages))
        : dispatch(setPdfPages(totalPdfPages));
    }
  };

  const isElementInViewport = (el) => {
    const totalWindowHeight = document.documentElement.clientHeight;
    const canvasContainerViewPortHeight = window.innerHeight;
    const viewHeight = Math.max(
      totalWindowHeight,
      canvasContainerViewPortHeight
    );
    const pageChangeBreakingPoint = viewHeight / 2;
    var pdfRect = el.getBoundingClientRect();

    return !!(
      pdfRect.bottom >= 0 &&
      pdfRect.top < pageChangeBreakingPoint &&
      pdfRect.bottom > pageChangeBreakingPoint
    );
  };

  const handlePDFPageNumberChangeOnScroll = () => {
    document.querySelector(".pdf-container").addEventListener("scroll", () => {
      const pageRefs = currentRef.current.filter((pageRef) => pageRef);
      pageRefs.forEach((el) => {
        if (el && isElementInViewport(el)) {
          const activePageNumber = parseInt(el.getAttribute("page-number"));
          activePageNumber && dispatch(setCurrentPage(activePageNumber));
        }
      });
    });
  };

  const handlePDFPageNavigation = (pageNumber) => {
    currentRef.current[pageNumber] &&
      currentRef.current[pageNumber].scrollIntoView({
        behavior: "smooth",
      });
  };

  /**
   *  Navigate to a specific page if page number is specified in a rowData
   */
  const handleInitialPageNavigationOnPDFLoad = () => {
    const pageNumber =
      (isPreviousAlertModal
        ? Number(selectedAlert?.pageNumber) + Number(coverPageLength)
        : isPreviousReportModal
          ? 1
          : autoScrollPageNumber) || 1;
    if (pageNumber && pageNumber > 1 && pageNumber <= getTotalPages()) {
      handlePDFPageNavigation(pageNumber);
    }
  };

  const handleLastPageLoadSuccess = () => {
    dispatch(pageLoadSuccess(true));
    handlePDFPageNumberChangeOnScroll();
    handleInitialPageNavigationOnPDFLoad();
  };

  const getTotalPages = () => {
    return isPreviousReport ? totalPreviousReportPdfPages : totalPdfPages;
  };

  return {
    dispatch,
    pdfFile,
    totalPdfPages,
    totalPreviousReportPdfPages,
    pdfPageRefs,
    showNotes,
    pdfWidth,
    openDrawer,
    isPdfLoading,
    isRegularPdfLoading,
    currentPageNumber,
    currentPageNumberForPreviousReport,
    currentRef,
    containerRef,
    openRegularReportOnNewTab,
    handlePDFPageNavigation,
    onPDFDocumentLoadSuccess,
    handleLastPageLoadSuccess,
    handlePDFPageNumberChangeOnScroll,
    fetchReportPdf,
    getTotalPages,
    checkIsFetchingCoverPage,
  };
};

export default usePdfViewer;
